import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { Kicker } from "../../ui/v2/Kicker";
import { BUTTON_RESET, pressClass } from "../../ui/v2/press";
import { scaledFont } from "@/styles/scaledFont";

export interface ClosestRowProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "title"
> {
  /** The row's kicker — `t('…')`, never a literal. */
  title: string;
  name: string;
  /** How they read today — ≤ 16 characters ("magnetic", "far today"). */
  tag: string;
  /** Opens the person's page in Friends. */
  onClick: () => void;
}

const BASE: CSSProperties = {
  ...BUTTON_RESET,
  display: "flex",
  alignItems: "center",
  gap: 14,
  minHeight: 76,
  width: "100%",
};

/* `400 22px/26px` display: the only Belleza on the screen below the day's own
 * line, and deliberately smaller than `--t-section` (24/28) — this row closes
 * the page, it does not open a section. */
const NAME: CSSProperties = {
  font: scaledFont("400 22px/26px var(--font-display)"),
  color: "var(--d-fg)",
};

const TAG: CSSProperties = { font: "var(--t-caption)", color: "var(--d-fg-2)" };

const CHEVRON: CSSProperties = {
  font: "400 18px var(--font-body)",
  color: "var(--d-fg-3)",
};

/**
 * "Closest today" — the day's tier-1 person, and the way into their page in
 * Friends.
 *
 * Ported from `ClosestRow` in
 * `docs/design/handoff/design/components/today/ClosestRow.jsx`; the
 * prototype's `<div onClick>` is a `<button>` here, per the v2 atoms.
 */
export function ClosestRow({
  title,
  name,
  tag,
  onClick,
  className,
  style,
  ...rest
}: ClosestRowProps): JSX.Element {
  return (
    <button
      type="button"
      className={pressClass(className)}
      onClick={onClick}
      style={{ ...BASE, ...style }}
      {...rest}
    >
      <span
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          textAlign: "left",
        }}
      >
        <Kicker>{title}</Kicker>
        <span data-closest-name style={NAME}>
          {name} <span data-closest-tag style={TAG}>{`· ${tag}`}</span>
        </span>
      </span>
      <span data-closest-chevron aria-hidden="true" style={CHEVRON}>
        ›
      </span>
    </button>
  );
}

export default ClosestRow;
